/**
 * CardDetailModal Component
 * Shows full card details in a modal — card art + domain icons and optional pricing.
 */

import { useMemo } from 'react';
import { DomainBadges } from './DomainIcon.jsx';
import { getCardImageUrl, getCardDomainsForDisplay, getIllustratorName, getCardSetDisplay } from './cardUtils.js';
import CardTextWithIcons from './CardTextWithIcons.jsx';
import { getRarityIconUrl } from './rarityIcons.js';
import { MIGHT_ICON_URL, POWER_ICON_URL } from './statIcons.js';
import { buildCardmarketUrl } from './cardmarketUrl.js';
import {
  readPriceSettings,
  formatPriceValue,
  computeRawPrices,
  periodLabel as getPeriodLabel
} from './priceUtils.js';
import { getDisplayPriceValue } from './filterOptions.js';

export default function CardDetailModal({ card, onClose }) {
  // All hooks must be called unconditionally (Rules of Hooks)
  const { showPrices, pricePeriod } = useMemo(() => readPriceSettings(), []);
  const cardmarketUrl = useMemo(
    () => (showPrices && card ? buildCardmarketUrl(card) : null),
    [card, showPrices]
  );

  if (!card) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const cardImageUrl = getCardImageUrl(card);
  const cardType = card.cardType?.type?.[0]?.label || 'Unknown';
  const superType = card.cardType?.superType?.[0]?.label || '';
  const rarity = card.rarity?.value?.label || 'Common';
  const domains = getCardDomainsForDisplay(card);
  const energy = card.energy?.value?.id ?? null;
  const power = card.power?.value?.id ?? null;
  const might = card.might?.value?.id ?? null;
  const illustrator = getIllustratorName(card);
  const setName = card.set?.value?.label || card.set?.value?.id || 'Unknown';
  const collectorNumber = card.collectorNumber || '';
  const setDisplay = getCardSetDisplay(card);
  const tags = card.tags?.tags || [];
  const rarityClass = `rarity-${String(rarity).toLowerCase().replace(/\s/g, '-')}`;
  const rarityIconUrl = getRarityIconUrl(card);
  const isPromo = !!card.promo;

  let normalPriceLabel = null;
  let foilPriceLabel = null;
  let summaryPriceLabel = null;
  if (showPrices && card.price) {
    const { rawNormal, rawFoil } = computeRawPrices(card, rarity);
    const base = getDisplayPriceValue(
      { ...card.price, normal: rawNormal, foil: rawFoil },
      rarity,
      cardType,
      isPromo
    );
    const normal = formatPriceValue(rawNormal);
    const foil = formatPriceValue(rawFoil);
    const summary = formatPriceValue(base);
    const pLabel = getPeriodLabel(pricePeriod);
    const hasNormal = rawNormal != null;
    const hasFoil = rawFoil != null;
    const showSeparateFoil = hasNormal && hasFoil;

    if (normal) normalPriceLabel = normal;
    if (foil && showSeparateFoil) foilPriceLabel = foil;
    if (summary) summaryPriceLabel = `${summary} · ${pLabel}`;
  }

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content card-detail-modal">
        <button className="modal-close" onClick={onClose}>×</button>

        <div className="card-detail-layout">
          <div className="card-detail-art">
            <div className="card-art-wrap">
              {cardImageUrl ? (
                <img
                  src={cardImageUrl}
                  alt={card.name || ''}
                  className="card-detail-art-image"
                />
              ) : (
                <div className="card-art-placeholder">
                  <div className="art-placeholder-content">
                    <div className="art-card-name">{card.name}</div>
                    <div className="art-card-id">{card.id}</div>
                    <div className="art-domains art-domains-icons">
                      <DomainBadges domains={domains.length ? domains : [{ label: 'Colourless' }]} />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="card-art-info">
              <div>Illustrated by {illustrator}</div>
              <div>{setDisplay || `${setName} #${collectorNumber}`}</div>
            </div>
          </div>

          {/* Right side - Card Details */}
          <div className="card-detail-info">
            {/* Header: name + promo badge + supertype (no cost here) */}
            <div className="detail-header">
              <div className="detail-header-top">
                <h2 className="detail-card-name">{card.name}</h2>
                {isPromo && (
                  <span className="detail-promo-badge" title="Promo card">
                    ★
                  </span>
                )}
              </div>
              {superType && <div className="detail-supertype">{superType}</div>}
            </div>

            {/* Type line: "Unit — [domain icons]" in one aligned row */}
            <div className="detail-type-line">
              <strong>{cardType}</strong>
              {domains.length > 0 && (
                <>
                  <span className="detail-domains-sep">—</span>
                  <DomainBadges domains={domains} />
                </>
              )}
            </div>

            {/* Card Text (rules/ability) with inline :rb_might: / :rb_power: icons */}
            <div className="detail-text-box">
              <div className="detail-text detail-text-with-icons">
                <CardTextWithIcons card={card} />
              </div>
            </div>

            {/* Compact stats: energy (cost), power, might — subtle, close together */}
            <div className="detail-stats">
              {energy !== null && energy !== undefined && (
                <div className="detail-stat detail-stat-energy">
                  <span className="stat-label">Cost</span>
                  <span className="stat-value">{energy}</span>
                </div>
              )}
              {power !== null && power !== undefined && (
                <div className="detail-stat detail-stat-power">
                  <img src={POWER_ICON_URL} alt="" className="stat-icon" aria-hidden />
                  <span className="stat-value">{power}</span>
                </div>
              )}
              {might !== null && might !== undefined && (
                <div className="detail-stat detail-stat-might">
                  <img src={MIGHT_ICON_URL} alt="" className="stat-icon" aria-hidden />
                  <span className="stat-value">{might}</span>
                </div>
              )}
            </div>

            {/* Pricing */}
            {showPrices && (summaryPriceLabel || normalPriceLabel || foilPriceLabel) && (
              <div className="detail-price-block">
                {summaryPriceLabel && (
                  <div className="detail-price-line detail-price-summary">
                    {summaryPriceLabel}
                  </div>
                )}
                {normalPriceLabel && (
                  <div className={`detail-price-line ${foilPriceLabel ? 'detail-price-line-normal-multi' : ''}`}>
                    {normalPriceLabel}
                  </div>
                )}
                {foilPriceLabel && (
                  <div className="detail-price-line detail-price-line-foil-multi">
                    {foilPriceLabel}
                  </div>
                )}
                {cardmarketUrl && (
                  <a
                    href={cardmarketUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="detail-cardmarket-link"
                  >
                    View on CardMarket
                  </a>
                )}
              </div>
            )}

            {/* Tags */}
            {tags.length > 0 && (
              <div className="detail-tags">
                <strong>Tags:</strong> {tags.join(', ')}
              </div>
            )}

            {/* Rarity (with icon when available) and Set */}
            <div className="detail-footer">
              <div className={`detail-rarity ${rarityClass}`}>
                <img src={rarityIconUrl} alt="" className="detail-rarity-icon" aria-hidden />
                <span>{rarity}</span>
              </div>
              <div className="detail-set">{setDisplay || card.id}</div>
            </div>

            {/* Notes */}
            {card.notes && (
              <div className="detail-notes">
                <strong>Notes:</strong> {card.notes}
              </div>
            )}

            {/* Errata */}
            {card.errata != null && card.errata !== '' && (
              <div className="detail-errata">
                <strong>Errata:</strong>{' '}
                {Array.isArray(card.errata) ? card.errata.join(' ') : String(card.errata)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
